import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {toast} from 'sonner';
import { Box, Paper, Typography, Button, Alert, CircularProgress, InputLabel, Stack, Grid2 } from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import config from '../../common/Config';

const BulkPriceUpdate = () => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const accessToken = localStorage.getItem('accessToken');
  const jwtToken = localStorage.getItem('token');

  const downloadSampleFile = () => {
    const sampleData = [
      {
        code: '0001',
        box_std_price1: '10.00',
        box_offer_price1: '9.50',
        piece_std_price1: '2.00',
        piece_offer_price1: '1.90',
        box_std_price2: '11.00',
        box_offer_price2: '10.50',
        piece_std_price2: '2.10',
        piece_offer_price2: '2.00',
        box_std_price3: '12.00',
        box_offer_price3: '11.50',
        piece_std_price3: '2.20',
        piece_offer_price3: '2.10',
        box_std_price4: '13.00',
        box_offer_price4: '12.50',
        piece_std_price4: '2.30',
        piece_offer_price4: '2.20',
        box_std_price5: '14.00',
        box_offer_price5: '13.50',
        piece_std_price5: '2.40',
        piece_offer_price5: '2.30',
        box_std_price6: '15.00',
        box_offer_price6: '14.50',
        piece_std_price6: '2.50',
        piece_offer_price6: '2.40'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bulk Price Update');
    XLSX.writeFile(wb, 'bulk_price_update_sample.xlsx');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension !== 'xlsx' && fileExtension !== 'xls' && fileExtension !== 'csv') {
        toast.error('Please upload a valid Excel file (.xlsx, .xls) or CSV file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const parseFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const validateData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return { valid: false, message: 'File is empty or invalid format' };
    }

    const requiredFields = ['code'];
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
    //  if (!row.code || row.code.toString().trim() === '') {
       // return { valid: false, message: `Row ${i + 2}: Product code is required` };
      //}
    }

    return { valid: true };
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setIsUploading(true);

    try {
      const data = await parseFile(file);
      const validation = validateData(data);

      if (!validation.valid) {
        toast.error(validation.message);
        setIsUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('data', JSON.stringify(data));

      const response = await fetch(`${config.API_URL}/products/bulk_update_prices`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${jwtToken || accessToken}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        toast.success(result.message || `Successfully updated prices for ${result.updated_count || data.length} products`);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        toast.error(result.message || 'Failed to update prices');
      }
    } catch (error) {
      toast.error('Failed to process file: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ m:2 }} border="1px solid #e0e0e0" borderRadius={2} p={2}  >
     
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Bulk Price Update
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
          Upload Excel or CSV file to update product prices in bulk
        </Typography>

        <Box sx={{ mb: 3 }}>
          {/*
          <Button
            variant="contained"
            size="medium"
            color="error"
            onClick={downloadSampleFile}
            sx={{ 
              fontSize: { xs: '0.8rem'},
              fontWeight: { xs: 500}, 
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' }
            }}
            startIcon={<Iconify icon="eva:download-outline" width={20} />}
          >
            Download Sample File
          </Button>
          */}
        </Box>

        <Box sx={{ mb: 3 }}>
          <InputLabel sx={{ mb: 1.5, fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600, color: 'text.primary' }}>
            Select File
          </InputLabel>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <input
              ref={fileInputRef}
              type="file"
              id="fileInput"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              component="label"
              htmlFor="fileInput"
              startIcon={<Iconify icon="eva:file-outline" width={20} />}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 500,
                py: 1.5,
                borderColor: 'rgba(43, 44, 46, 0.79)',
                color: 'text.secondary',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                '&:hover': {
                  borderColor: 'rgba(145, 158, 171, 0.48)',
                  backgroundColor: 'rgba(145, 158, 171, 0.08)'
                }
              }}
            >
              {file ? file.name : 'Choose file (.xlsx, .xls, .csv)'}
            </Button>
            <Button
              variant="contained"
              size="medium"

              sx={{ 
                fontWeight: 600, 
                textTransform: 'none', 
                px: { xs: 2, sm: 3 },
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: "#F8991D",
                "&:hover": {
                  backgroundColor: "#F8991D",
                  boxShadow: "none",
                },
              }}
              onClick={handleUpload}
              disabled={!file || isUploading}
              startIcon={
                isUploading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Iconify icon="eva:upload-outline" width={20} />
                )
              }
            >
              {isUploading ? 'Processing' : 'Upload & Update'}
            </Button>
          </Stack>
        </Box>

        {file && (
          <Alert 
            severity="info" 
            sx={{ 
              py: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'rgba(33, 150, 243, 0.08)',
              border: '1px solid rgba(33, 150, 243, 0.16)'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 0.5, sm: 1.5 }
            }}>
              <Iconify icon="eva:file-text-outline" width={20} sx={{ color: 'info.main' }} />
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: 'text.primary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                ({(file.size / 1024).toFixed(2)} KB)
              </Typography>
            </Box>
          </Alert>
        )}
    </Box>
  );
};

export default BulkPriceUpdate;

